import { Injectable } from '@angular/core';
import { EmpresaService } from './empresa.service';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class TicketImpresionService {

  constructor(private empresaService: EmpresaService) { }

  /**
   * Imprime un ticket de venta en formato térmico
   * @param ventaData Datos de la venta para imprimir
   */
  imprimirTicketVenta(ventaData: any): void {
    // Obtener información de la empresa y luego generar el ticket
    this.empresaService.getEmpresaConfig().pipe(
      catchError(error => {
        console.warn('No se pudo obtener información de la empresa, usando valores por defecto:', error);
        return of({
          socialReason: 'KOCURSOFT',
          commercialAddress: 'Sistema ERP PYMES',
          phone: '',
          email: ''
        });
      })
    ).subscribe(empresa => {
      const ticketContent = this.generarContenidoTicket(ventaData, empresa);
      this.imprimirContenido(ticketContent);
    });
  }

  /**
   * Genera el contenido HTML del ticket de venta
   * @param ventaData Datos de la venta
   * @param empresaData Datos de la empresa
   * @returns Contenido HTML formateado para ticket
   */
  private generarContenidoTicket(ventaData: any, empresaData: any = null): string {
    const fecha = new Date().toLocaleDateString('es-AR');
    const hora = new Date().toLocaleTimeString('es-AR', { hour12: false });
    
    // Usar datos de la empresa si están disponibles, sino valores por defecto
    const nombreEmpresa = empresaData?.socialReason || '';
    const direccionEmpresa = empresaData?.commercialAddress || '';
    const documentoEmpresa = empresaData?.documentNumber ? `CUIT: ${empresaData.documentNumber}` : '';
    
    let ticketHTML = `
      <div style="width: 280px; font-family: 'Courier New', monospace; font-size: 12px; line-height: 1.2;">
        <div style="text-align: center; font-weight: bold; border-bottom: 1px dashed #000; padding-bottom: 8px; margin-bottom: 8px;">
          <div style="font-size: 14px;">${nombreEmpresa}</div>
          <div style="font-size: 11px;">${direccionEmpresa}</div>
          ${documentoEmpresa ? `<div style="font-size: 10px;">${documentoEmpresa}</div>` : ''}
        </div>
        
        <div style="text-align: center; margin-bottom: 10px;">
          <div style="font-weight: bold;">TICKET DE VENTA</div>
          <div style="font-size: 10px;">Fecha: ${fecha} - Hora: ${hora}</div>
        </div>
        
        <div style="margin-bottom: 10px;">
          <div><strong>Cliente:</strong> ${ventaData.cliente || 'Consumidor Final'}</div>
          <div><strong>Vendedor:</strong> ${ventaData.vendedor || 'N/A'}</div>
          <div><strong>Punto Venta:</strong> ${ventaData.puntoVenta || 'N/A'}</div>
          ${ventaData.numeroComprobante ? `<div><strong>Nº Comprobante:</strong> ${ventaData.numeroComprobante}</div>` : ''}
        </div>
        
        <div style="border-top: 1px dashed #000; border-bottom: 1px dashed #000; padding: 5px 0; margin: 10px 0;">
          <div style="display: flex; justify-content: space-between; font-weight: bold; font-size: 10px;">
            <span style="width: 120px;">DESCRIPCIÓN</span>
            <span style="width: 30px; text-align: center;">CANT</span>
            <span style="width: 50px; text-align: right;">PRECIO</span>
            <span style="width: 60px; text-align: right;">TOTAL</span>
          </div>
        </div>
        
        <div style="margin-bottom: 10px;">`;

    // Agregar productos
    if (ventaData.productos && ventaData.productos.length > 0) {
      ventaData.productos.forEach(producto => {
        const descripcionCompleta = producto.description || '';
        const descripcion = descripcionCompleta.substring(0, 20);
        const cantidad = producto.quantity || 0;
        const precio = this.formatearMoneda(producto.unitPrice || 0);
        const total = this.formatearMoneda(producto.subtotalWithIva || 0);
        
        // Aplicar padding adicional si la descripción es mayor a 21 caracteres
        const paddingExtra = descripcionCompleta.length > 21 ? 'padding-top: 3px;' : '';
        
        ticketHTML += `
          <div style="display: flex; justify-content: space-between; font-size: 10px; margin-bottom: 2px;">
            <span style="width: 120px; word-wrap: break-word;">${descripcion}</span>
            <span style="width: 30px; text-align: center; ${paddingExtra}">${cantidad}</span>
            <span style="width: 50px; text-align: right; ${paddingExtra}">$${precio}</span>
            <span style="width: 60px; text-align: right; ${paddingExtra}">$${total}</span>
          </div>`;
      });
    }

    ticketHTML += `
        </div>
        
        <div style="border-top: 1px dashed #000; padding-top: 8px; margin-top: 10px;">
          <div style="display: flex; justify-content: space-between; font-weight: bold; font-size: 12px;">
            <span>TOTAL:</span>
            <span>$${this.formatearMoneda(ventaData.total || 0)}</span>
          </div>
        </div>`;

    // Agregar formas de pago si existen
    if (ventaData.formasPago && ventaData.formasPago.length > 0) {
      ticketHTML += `
        <div style="margin-top: 8px; border-top: 1px dashed #000; padding-top: 8px;">
          <div style="font-weight: bold; font-size: 10px; margin-bottom: 4px;">FORMAS DE PAGO:</div>`;
      
      ventaData.formasPago.forEach(pago => {
        ticketHTML += `
          <div style="display: flex; justify-content: space-between; font-size: 10px;">
            <span>${pago.descripcion || 'Efectivo'}</span>
            <span>$${this.formatearMoneda(pago.monto || 0)}</span>
          </div>`;
      });
      
      ticketHTML += `</div>`;
    }

    // Agregar observaciones si existen
    if (ventaData.observaciones) {
      ticketHTML += `
        <div style="margin-top: 8px; border-top: 1px dashed #000; padding-top: 8px;">
          <div style="font-size: 10px;"><strong>Observaciones:</strong></div>
          <div style="font-size: 10px;">${ventaData.observaciones}</div>
        </div>`;
    }

    ticketHTML += `
        <div style="text-align: center; margin-top: 15px; font-size: 10px;">
          <div>¡Gracias por su compra!</div>
         </div>
      </div>`;

    return ticketHTML;
  }

  /**
   * Envía el contenido a imprimir
   * @param contenido Contenido HTML del ticket
   */
  private imprimirContenido(contenido: string): void {
    const ventanaImpresion = window.open('', '_blank');
    
    if (ventanaImpresion) {
      ventanaImpresion.document.write(`
        <html>
          <head>
            <title>Ticket de Venta</title>
            <style>
              @media print {
                body { margin: 0; padding: 0; }
                @page { size: 80mm auto; margin: 0; }
              }
              body {
                margin: 0;
                padding: 10px;
                background: white;
              }
            </style>
          </head>
          <body>
            ${contenido}
            <script>
              window.onload = function() {
                setTimeout(function() {
                  window.print();
                  window.close();
                }, 100);
              };
            </script>
          </body>
        </html>
      `);
      
      ventanaImpresion.document.close();
    } else {
      alert('No se pudo abrir la ventana de impresión. Verifique que no esté bloqueada por el navegador.');
    }
  }

  /**
   * Formatea un número como moneda
   * @param valor Valor numérico
   * @returns String formateado como moneda
   */
  private formatearMoneda(valor: number): string {
    return new Intl.NumberFormat('es-AR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(valor || 0);
  }
}
